import React, { useState, useEffect } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment-timezone';

const ITEMS_PER_PAGE = 50; 

const Pagination = ({ totalItems, currentPage, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    if (totalPages <= 1) return null;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 3) {
        endPage = Math.min(5, totalPages);
    } else if (currentPage + 2 >= totalPages) {
        startPage = Math.max(1, totalPages - 4);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', padding: '10px' }}>
            <button 
                disabled={currentPage === 1} 
                onClick={() => onPageChange(currentPage - 1)}
                style={{ padding: '6px 12px', border: '1px solid #cbd5e1', background: currentPage === 1 ? '#f8fafc' : 'white', color: currentPage === 1 ? '#94a3b8' : '#0f172a', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', borderRadius: '6px', fontWeight: 'bold' }}
            >Prev</button>
            
            {pages.map(p => (
                <button 
                    key={p} 
                    onClick={() => onPageChange(p)}
                    style={{ padding: '6px 14px', border: '1px solid #0284c7', background: currentPage === p ? '#0284c7' : 'white', color: currentPage === p ? 'white' : '#0284c7', cursor: 'pointer', borderRadius: '6px', fontWeight: 'bold', transition: '0.2s' }}
                >{p}</button>
            ))}

            <button 
                disabled={currentPage === totalPages} 
                onClick={() => onPageChange(currentPage + 1)}
                style={{ padding: '6px 12px', border: '1px solid #cbd5e1', background: currentPage === totalPages ? '#f8fafc' : 'white', color: currentPage === totalPages ? '#94a3b8' : '#0f172a', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', borderRadius: '6px', fontWeight: 'bold' }}
            >Next</button>
        </div>
    );
};

const AccountTaskManager = () => {
    const [activeTab, setActiveTab] = useState('account');
    
    // Auth & Permission
    const isMaster = localStorage.getItem('isMaster') === 'true';
    const adminId = localStorage.getItem('adminId2');

    // States - added tmDebit for This Month Debit
    const [balance, setBalance] = useState(0);
    const [historyList, setHistoryList] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [stats, setStats] = useState({ tCredit: 0, tDebit: 0, lmCredit: 0, lmDebit: 0, tmDebit: 0 }); 
    
    const [adminStatsList, setAdminStatsList] = useState([]); 
    const [viewAdminId, setViewAdminId] = useState(null); 
    const [viewAdminData, setViewAdminData] = useState({ history: [], tasks: [] }); 

    // Pagination States
    const [viewAdminTab, setViewAdminTab] = useState('history'); 
    const [viewHistoryPage, setViewHistoryPage] = useState(1);
    const [viewTaskPage, setViewTaskPage] = useState(1);
    
    const [myHistoryPage, setMyHistoryPage] = useState(1);
    const [doneTaskPage, setDoneTaskPage] = useState(1);
    const [masterTaskPage, setMasterTaskPage] = useState(1); // Master এর Task List এর জন্য

    const [transaction, setTransaction] = useState({ userId: '', amount: '', type: 'credit', note: '' });
    const [newTask, setNewTask] = useState({ userId: '', task_name: '', task_note: '' });
    const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });

    const [updatingTaskId, setUpdatingTaskId] = useState(null);
    const [updateNoteText, setUpdateNoteText] = useState('');

    useEffect(() => {
        const fetchInitialData = () => {
            if (!adminId) return; 
            socketService.emit('fetch-account-data', { adminId, isMaster });
            if (isMaster) {
                socketService.emit('fetch-admin-stats'); 
            }
        };

        const socket = socketService.getSocket ? socketService.getSocket() : null;
        if (socket && socket.connected) {
            fetchInitialData();
        } else {
            if (socketService.connect) {
                socketService.connect();
            }
        }

        socketService.on('connect', fetchInitialData);

        socketService.on('account-data-response', (data) => {
            setBalance(data.balance || 0);
            setHistoryList(data.history || []);
            setTasks(data.tasks || []);
            
            if (!isMaster && data.history) {
                calculateStats(data.history);
            }
        });

        socketService.on('admin-stats-response', (data) => {
            setAdminStatsList(data.adminStats || []); 
        });

        socketService.on('view-admin-data-response', (data) => {
            setViewAdminData({ history: data.history || [], tasks: data.tasks || [] });
        });

        socketService.on('action-success', (msg) => {
            alert(msg);
            fetchInitialData(); 
            if (viewAdminId) {
                socketService.emit('fetch-view-admin-data', { targetAdminId: viewAdminId });
            }
            setUpdatingTaskId(null);
            setUpdateNoteText('');
        });

        return () => {
            socketService.off('connect', fetchInitialData);
            socketService.off('account-data-response');
            socketService.off('admin-stats-response');
            socketService.off('view-admin-data-response');
            socketService.off('action-success');
        };
    }, [viewAdminId, adminId, isMaster]);

    // calculateStats updated to include tmDebit
    const calculateStats = (history) => {
        const now = moment().tz('Asia/Dhaka');
        const lastMonthStart = now.clone().subtract(1, 'month').startOf('month');
        const thisMonthStart = now.clone().startOf('month');

        let tCredit = 0, tDebit = 0, lmCredit = 0, lmDebit = 0, tmDebit = 0;

        history.forEach(h => {
            const amount = Number(h.amount);
            const date = moment(h.createdAt);
            if (!Number.isFinite(amount) || !date.isValid()) return;

            if (h.transaction_type === 'credit') {
                tCredit += amount;
                if (date.isSameOrAfter(lastMonthStart) && date.isBefore(thisMonthStart)) lmCredit += amount;
            } else if (h.transaction_type === 'debit') {
                tDebit += amount;
                if (date.isSameOrAfter(thisMonthStart)) tmDebit += amount; // This month debit
                else if (date.isSameOrAfter(lastMonthStart) && date.isBefore(thisMonthStart)) lmDebit += amount;
            }
        });

        setStats({ tCredit, tDebit, lmCredit, lmDebit, tmDebit });
    };

    const handleTransactionSubmit = (e) => {
        e.preventDefault();
        if (!transaction.userId || !transaction.amount || !transaction.note) return alert("সব ফিল্ড পূরণ করো");
        socketService.emit('process-transaction', { ...transaction, adminBy: adminId });
        setTransaction({ userId: '', amount: '', type: 'credit', note: '' });
    };

    const handleHistoryFilter = () => {
        setMyHistoryPage(1); 
        socketService.emit('filter-history', { adminId, isMaster, ...dateFilter });
    };

    const handleAddTask = (e) => {
        e.preventDefault();
        if (!newTask.userId || !newTask.task_name || !newTask.task_note) return alert("সব ফিল্ড পূরণ করো");
        socketService.emit('add-new-task', { ...newTask, createdBy: adminId });
        setNewTask({ userId: '', task_name: '', task_note: '' });
    };

    const handleDeleteTask = (taskId) => {
        if(window.confirm("টাস্কটি ডিলিট করতে চাও?")) {
            socketService.emit('delete-task', { taskId });
        }
    };

    const handleCompleteTask = () => {
        if (!updateNoteText) return alert("আপডেট নোট লেখো!");
        socketService.emit('update-task-status', { taskId: updatingTaskId, status: 'done', update_note: updateNoteText });
    };

    const handleViewAdminClick = (id) => {
        setViewAdminId(id);
        setViewAdminTab('history');
        setViewHistoryPage(1);
        setViewTaskPage(1);
        socketService.emit('fetch-view-admin-data', { targetAdminId: id });
    };

    const getAdminName = (id) => {
        const matched = adminStatsList.find(a => a._id === (id?._id || id));
        return matched ? matched.username : 'Unknown Admin';
    };

    // Data Slicing
    const paginatedMyHistory = historyList.slice((myHistoryPage - 1) * ITEMS_PER_PAGE, myHistoryPage * ITEMS_PER_PAGE);
    const paginatedViewHistory = viewAdminData.history.slice((viewHistoryPage - 1) * ITEMS_PER_PAGE, viewHistoryPage * ITEMS_PER_PAGE);
    const paginatedViewTasks = viewAdminData.tasks.slice((viewTaskPage - 1) * ITEMS_PER_PAGE, viewTaskPage * ITEMS_PER_PAGE);
    
    // Task slicing for Regular Admin
    const pendingTasks = tasks.filter(t => t.status === 'pending');
    const doneTasks = tasks.filter(t => t.status === 'done');
    const paginatedDoneTasks = doneTasks.slice((doneTaskPage - 1) * ITEMS_PER_PAGE, doneTaskPage * ITEMS_PER_PAGE);

    // Task Slicing for Master Admin
    const paginatedMasterTasks = tasks.slice((masterTaskPage - 1) * ITEMS_PER_PAGE, masterTaskPage * ITEMS_PER_PAGE);

    const activeTabStyle = { background: '#0284c7', color: 'white' };

    return (
        <Layout>
            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; padding: 20px; }
                .nav-tabs-container { display: flex; background: #ffffff; padding: 15px 20px; border-bottom: 1px solid #e2e8f0; margin-bottom: 20px; gap: 15px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);}
                .nav-tab-item { padding: 10px 25px; font-size: 14px; font-weight: 800; color: #0284c7; border: 1px solid #0284c7; background: #f0f9ff; border-radius: 6px; text-transform: uppercase; cursor: pointer; transition: 0.3s; }
                .nav-tab-item:hover { background: #e0f2fe; }
                .sub-tab-container { display: flex; gap: 10px; padding: 15px; border-bottom: 2px solid #e2e8f0; background: #f8fafc; }
                .sub-tab-item { padding: 8px 20px; font-size: 13px; font-weight: bold; border-radius: 20px; border: 1px solid #cbd5e1; cursor: pointer; background: white; color: #475569; transition: 0.2s;}
                .sub-tab-item.active { background: #0369a1; color: white; border-color: #0369a1; }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; padding: 0; box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin-bottom: 20px; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #0284c7; color: #ffffff; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; text-align: left; }
                .custom-zebra-table tbody tr:nth-child(even) { background-color: #f8fafc; }
                .custom-zebra-table tbody tr:hover { background-color: #f1f5f9; }
                .custom-zebra-table td { padding: 12px 15px; vertical-align: middle; border-bottom: 1px solid #edf2f7; }
                .user-primary { color: #0369a1; font-weight: 700; font-size: 14px; }
                .time-muted { font-size: 12px; color: #64748b; }
                .date-bold { font-weight: 700; font-size: 14px; }
                .msg-body { color: #334155; font-size: 13px; }
                .badge-status { padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 900; border: 1px solid transparent; display: inline-block; }
                .bg-seen { background: #16a34a; color: white; }
                .bg-fake { background: #fee2e2; color: #dc2626; }
                .bg-pending { background: #fef08a; color: #854d0e; }
                .bg-credit { background: #dcfce7; color: #166534; }
                .custom-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(15, 23, 42, 0.6); z-index: 2000; display: flex; justify-content: center; align-items: center; backdrop-filter: blur(3px); }
                .custom-modal { background: white; width: 90%; max-width: 420px; border-radius: 16px; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
                .modal-header-custom { padding: 16px 24px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; }
                .modal-header-custom h5 { margin: 0; font-weight: 700; color: #334155; }
                .modal-body-custom { padding: 24px; }
                .field-label { font-size: 12px; font-weight: 700; color: #64748b; margin-bottom: 5px; display: block; }
                .modern-input { width: 100%; background: #f8fafc; border: 1px solid #cbd5e1; padding: 10px; border-radius: 8px; font-size: 14px; outline: none; box-sizing: border-box; }
                .modern-input:focus { border-color: #0284c7; }
                .modal-footer-custom { padding: 16px 24px; background: #f8fafc; border-top: 1px solid #f1f5f9; display: flex; gap: 12px; }
                .modal-btn { border: none; padding: 10px 0; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; flex: 1; text-align: center;}
                .modal-btn.cancel { background: #e2e8f0; color: #475569; }
                .modal-btn.confirm { background: #0284c7; color: white; }
                .form-panel { background: white; padding: 20px; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
                .task-card { background: white; border: 1px solid #e2e8f0; border-radius: 10px; padding: 15px; margin-bottom: 15px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); border-left: 4px solid #0284c7;}
                .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 20px; }
                .stat-box { padding: 20px; border-radius: 12px; color: white; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
            `}</style>

            <div className="main-content-area">
                <h3 style={{ color: '#0f172a', fontWeight: '800', marginBottom: '20px' }}>Account & Task Manager</h3>

                <div className="nav-tabs-container">
                    <button className="nav-tab-item" style={activeTab === 'account' ? activeTabStyle : {}} onClick={() => { setActiveTab('account'); setViewAdminId(null); }}>Account</button>
                    {!isMaster && <button className="nav-tab-item" style={activeTab === 'history' ? activeTabStyle : {}} onClick={() => setActiveTab('history')}>My History</button>}
                    <button className="nav-tab-item" style={activeTab === 'tasks' ? activeTabStyle : {}} onClick={() => setActiveTab('tasks')}>Tasks</button>
                </div>

                {/* ================= ACCOUNT TAB ================= */}
                {activeTab === 'account' && (
                    <div>
                        {isMaster ? (
                            <>
                                {!viewAdminId ? (
                                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                        <div className="table-container" style={{ flex: '1 1 60%', padding: '15px' }}>
                                            <div style={{ paddingBottom: '15px', fontWeight: '800', color: '#0369a1' }}>ADMIN LIST</div>
                                            <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                                {/* Updated Table Headings */}
                                                <table className="custom-zebra-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Admin</th>
                                                            <th>Balance</th>
                                                            <th>Last Month (Cr/Dr)</th>
                                                            <th>This Month (Cr/Dr)</th>
                                                            <th>Tasks (P/D)</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {adminStatsList.map((admin, i) => (
                                                            <tr key={i}>
                                                                <td className="user-primary">{admin.username}</td>
                                                                <td className="date-bold" style={{ color: '#16a34a' }}>৳ {admin.balance || 0}</td>
                                                                <td>
                                                                    <span style={{color: '#16a34a', fontWeight: 'bold'}}>+{admin.lmCredit || 0}</span> <br/> 
                                                                    <span style={{color: '#dc2626', fontWeight: 'bold'}}>-{admin.lmDebit || 0}</span>
                                                                </td>
                                                                <td>
                                                                    <span style={{color: '#16a34a', fontWeight: 'bold'}}>+{admin.tmCredit || 0}</span> <br/> 
                                                                    <span style={{color: '#dc2626', fontWeight: 'bold'}}>-{admin.tmDebit || 0}</span>
                                                                </td>
                                                                <td>
                                                                    <span className="badge-status bg-pending" style={{ marginRight: '5px' }}>{admin.pendingTasks || 0} P</span>
                                                                    <span className="badge-status bg-seen">{admin.doneTasks || 0} D</span>
                                                                </td>
                                                                <td>
                                                                    <button onClick={() => handleViewAdminClick(admin._id)} style={{ background: '#0284c7', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>View</button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <div className="form-panel" style={{ flex: '1 1 30%' }}>
                                            <h5 style={{ marginTop: 0, color: '#0369a1', fontWeight: '800' }}>Add Transaction</h5>
                                            <form onSubmit={handleTransactionSubmit}>
                                                <label className="field-label mt-3">Select Admin</label>
                                                <select className="modern-input" value={transaction.userId} onChange={e => setTransaction({...transaction, userId: e.target.value})}>
                                                    <option value="">-- Select --</option>
                                                    {adminStatsList.map(u => <option key={u._id} value={u._id}>{u.username}</option>)}
                                                </select>
                                                
                                                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <label className="field-label">Amount</label>
                                                        <input type="number" className="modern-input" value={transaction.amount} onChange={e => setTransaction({...transaction, amount: e.target.value})} />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <label className="field-label">Type</label>
                                                        <select className="modern-input" value={transaction.type} onChange={e => setTransaction({...transaction, type: e.target.value})}>
                                                            <option value="credit">Credit (+)</option>
                                                            <option value="debit">Debit (-)</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <label className="field-label" style={{ marginTop: '10px' }}>Note</label>
                                                <input type="text" className="modern-input mb-3" value={transaction.note} onChange={e => setTransaction({...transaction, note: e.target.value})} />
                                                <button type="submit" className="modal-btn confirm" style={{ width: '100%', marginTop: '15px' }}>Submit</button>
                                            </form>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="table-container" style={{ borderTop: '4px solid #0284c7' }}>
                                        <div style={{ padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white' }}>
                                            <h5 style={{ margin: 0, color: '#0f172a', fontWeight: '900' }}>Admin Records & Logs</h5>
                                            <button onClick={() => setViewAdminId(null)} style={{ background: '#e2e8f0', color: '#334155', border: 'none', padding: '6px 15px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>⬅ Back to List</button>
                                        </div>
                                        
                                        <div className="sub-tab-container">
                                            <button className={`sub-tab-item ${viewAdminTab === 'history' ? 'active' : ''}`} onClick={() => setViewAdminTab('history')}>Transaction History ({viewAdminData.history.length})</button>
                                            <button className={`sub-tab-item ${viewAdminTab === 'tasks' ? 'active' : ''}`} onClick={() => setViewAdminTab('tasks')}>Task History ({viewAdminData.tasks.length})</button>
                                        </div>

                                        {viewAdminTab === 'history' && (
                                            <div>
                                                <table className="custom-zebra-table">
                                                    <thead>
                                                        <tr><th>Date</th><th>Type</th><th>Amount</th><th>Note</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedViewHistory.map((h, i) => (
                                                            <tr key={i}>
                                                                <td className="time-muted">{new Date(h.createdAt).toLocaleDateString()}</td>
                                                                <td><span className={`badge-status ${h.transaction_type === 'credit' ? 'bg-credit' : 'bg-fake'}`}>{h.transaction_type}</span></td>
                                                                <td className="date-bold">৳ {h.amount}</td>
                                                                <td className="msg-body">{h.note}</td>
                                                            </tr>
                                                        ))}
                                                        {paginatedViewHistory.length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>No history found</td></tr>}
                                                    </tbody>
                                                </table>
                                                <Pagination totalItems={viewAdminData.history.length} currentPage={viewHistoryPage} onPageChange={setViewHistoryPage} />
                                            </div>
                                        )}

                                        {viewAdminTab === 'tasks' && (
                                            <div>
                                                <table className="custom-zebra-table">
                                                    <thead>
                                                        <tr><th>Date</th><th>Task Name</th><th>Details / Note</th><th>Status</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedViewTasks.map((t, i) => (
                                                            <tr key={i}>
                                                                <td className="time-muted">{new Date(t.createdAt).toLocaleDateString()}</td>
                                                                <td className="user-primary" style={{ textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>{t.task_name}</td>
                                                                <td>
                                                                    <div className="msg-body" style={{fontSize: '12px', marginBottom: '4px'}}><strong>Task:</strong> {t.task_note}</div>
                                                                    {t.status === 'done' && <div className="msg-body" style={{fontSize: '12px', color: '#16a34a'}}><strong>Done Note:</strong> {t.update_note}</div>}
                                                                </td>
                                                                <td><span className={`badge-status ${t.status === 'done' ? 'bg-seen' : 'bg-pending'}`}>{t.status.toUpperCase()}</span></td>
                                                            </tr>
                                                        ))}
                                                        {paginatedViewTasks.length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>No tasks found</td></tr>}
                                                    </tbody>
                                                </table>
                                                <Pagination totalItems={viewAdminData.tasks.length} currentPage={viewTaskPage} onPageChange={setViewTaskPage} />
                                            </div>
                                        )}
                                        <div style={{ height: '20px' }}></div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div>
                                <div className="stats-grid">
                                    <div className="stat-box" style={{ background: '#0ea5e9' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.9 }}>My Current Balance</span>
                                        <h2 style={{ margin: '5px 0 0 0', fontWeight: '800' }}>৳ {balance.toLocaleString()}</h2>
                                    </div>
                                    
                                    {/* Added My Current Debit Box */}
                                    <div className="stat-box" style={{ background: '#f59e0b' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.9 }}>My Current Debit</span>
                                        <h2 style={{ margin: '5px 0 0 0', fontWeight: '800' }}>৳ {stats.tmDebit.toLocaleString()}</h2>
                                    </div>

                                    <div className="stat-box" style={{ background: '#10b981' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.9 }}>Total Credit (All Time)</span>
                                        <h3 style={{ margin: '5px 0 0 0', fontWeight: '800' }}>৳ {stats.tCredit.toLocaleString()}</h3>
                                    </div>
                                    <div className="stat-box" style={{ background: '#f43f5e' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.9 }}>Total Debit (All Time)</span>
                                        <h3 style={{ margin: '5px 0 0 0', fontWeight: '800' }}>৳ {stats.tDebit.toLocaleString()}</h3>
                                    </div>
                                    <div className="stat-box" style={{ background: '#059669' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.9 }}>Last Month Credit</span>
                                        <h3 style={{ margin: '5px 0 0 0', fontWeight: '800' }}>৳ {stats.lmCredit.toLocaleString()}</h3>
                                    </div>
                                    <div className="stat-box" style={{ background: '#e11d48' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.9 }}>Last Month Debit</span>
                                        <h3 style={{ margin: '5px 0 0 0', fontWeight: '800' }}>৳ {stats.lmDebit.toLocaleString()}</h3>
                                    </div>
                                </div>

                                <div className="table-container">
                                    <h5 style={{ padding: '15px', margin: 0, color: '#0369a1', fontWeight: '800' }}>Recent Transactions</h5>
                                    <table className="custom-zebra-table">
                                        <thead>
                                            <tr><th>Date</th><th>Type</th><th>Amount</th><th>Note</th></tr>
                                        </thead>
                                        <tbody>
                                            {historyList.slice(0, 10).map((h, i) => (
                                                <tr key={i}>
                                                    <td className="time-muted">{new Date(h.createdAt).toLocaleDateString()}</td>
                                                    <td><span className={`badge-status ${h.transaction_type === 'credit' ? 'bg-credit' : 'bg-fake'}`}>{h.transaction_type}</span></td>
                                                    <td className="date-bold">৳ {h.amount}</td>
                                                    <td className="msg-body">{h.note}</td>
                                                </tr>
                                            ))}
                                            {historyList.length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>No transactions found.</td></tr>}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ================= HISTORY TAB (For Non-Master) ================= */}
                {activeTab === 'history' && !isMaster && (
                    <div className="table-container">
                        <div style={{ padding: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <span style={{ fontWeight: '800', color: '#0369a1', flexGrow: 1 }}>FILTER HISTORY</span>
                            <input type="date" className="modern-input" style={{ width: 'auto' }} value={dateFilter.startDate} onChange={e => setDateFilter({...dateFilter, startDate: e.target.value})} />
                            <input type="date" className="modern-input" style={{ width: 'auto' }} value={dateFilter.endDate} onChange={e => setDateFilter({...dateFilter, endDate: e.target.value})} />
                            <button className="modal-btn confirm" style={{ padding: '10px 20px', flex: 'none' }} onClick={handleHistoryFilter}>Filter</button>
                        </div>
                        <table className="custom-zebra-table">
                            <thead>
                                <tr><th>Date</th><th>Type</th><th>Amount</th><th>Note</th></tr>
                            </thead>
                            <tbody>
                                {paginatedMyHistory.map((h, i) => (
                                    <tr key={i}>
                                        <td className="time-muted">{new Date(h.createdAt).toLocaleDateString()}</td>
                                        <td><span className={`badge-status ${h.transaction_type === 'credit' ? 'bg-credit' : 'bg-fake'}`}>{h.transaction_type}</span></td>
                                        <td className="date-bold">৳ {h.amount}</td>
                                        <td className="msg-body">{h.note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Pagination totalItems={historyList.length} currentPage={myHistoryPage} onPageChange={setMyHistoryPage} />
                        <div style={{ height: '20px' }}></div>
                    </div>
                )}

                {/* ================= TASK TAB ================= */}
                {activeTab === 'tasks' && (
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        
                        {/* ---------------- MASTER VIEW FOR TASKS ---------------- */}
                        {isMaster ? (
                            <>
                                <div className="table-container" style={{ flex: '1 1 60%', padding: '15px' }}>
                                    <h5 style={{ fontWeight: '800', color: '#0369a1', marginBottom: '15px' }}>All Assigned Tasks</h5>
                                    <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                        <table className="custom-zebra-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Assigned To</th>
                                                    <th>Task Details</th>
                                                    <th>Status</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedMasterTasks.map(task => (
                                                    <tr key={task._id}>
                                                        <td className="time-muted">{new Date(task.createdAt).toLocaleDateString()}</td>
                                                        <td className="user-primary">{getAdminName(task.admin_id)}</td>
                                                        <td>
                                                            <div className="date-bold">{task.task_name}</div>
                                                            <div className="msg-body" style={{fontSize: '12px'}}>{task.task_note}</div>
                                                            {task.status === 'done' && <div style={{fontSize: '12px', color: '#16a34a', marginTop: '4px'}}><strong>Update:</strong> {task.update_note}</div>}
                                                        </td>
                                                        <td>
                                                            <span className={`badge-status ${task.status === 'done' ? 'bg-seen' : 'bg-pending'}`}>
                                                                {task.status.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }} onClick={() => handleDeleteTask(task._id)}>Delete</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {paginatedMasterTasks.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>No tasks found.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Pagination totalItems={tasks.length} currentPage={masterTaskPage} onPageChange={setMasterTaskPage} />
                                </div>

                                <div className="form-panel" style={{ flex: '1 1 30%', alignSelf: 'flex-start' }}>
                                    <h5 style={{ marginTop: 0, color: '#0369a1', fontWeight: '800' }}>Assign New Task</h5>
                                    <form onSubmit={handleAddTask}>
                                        <label className="field-label mt-3">Assign To</label>
                                        <select className="modern-input" value={newTask.userId} onChange={e => setNewTask({...newTask, userId: e.target.value})}>
                                            <option value="">-- Select Admin --</option>
                                            {adminStatsList.map(u => <option key={u._id} value={u._id}>{u.username}</option>)}
                                        </select>
                                        <label className="field-label" style={{ marginTop: '10px' }}>Task Name</label>
                                        <input type="text" className="modern-input" value={newTask.task_name} onChange={e => setNewTask({...newTask, task_name: e.target.value})} />
                                        <label className="field-label" style={{ marginTop: '10px' }}>Task Note</label>
                                        <textarea className="modern-input" rows="3" value={newTask.task_note} onChange={e => setNewTask({...newTask, task_note: e.target.value})}></textarea>
                                        <button type="submit" className="modal-btn confirm" style={{ width: '100%', marginTop: '15px' }}>Create Task</button>
                                    </form>
                                </div>
                            </>
                        ) : (
                            /* ---------------- REGULAR ADMIN VIEW FOR TASKS ---------------- */
                            <div style={{ flex: '1 1 100%' }}>
                                <h5 style={{ fontWeight: '800', color: '#854d0e', marginBottom: '15px' }}>Pending Tasks</h5>
                                {pendingTasks.map(task => (
                                    <div key={task._id} className="task-card">
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <h5 className="user-primary" style={{ margin: 0 }}>{task.task_name}</h5>
                                        </div>
                                        <p className="msg-body" style={{ marginTop: '10px' }}>{task.task_note}</p>
                                        <button className="modal-btn confirm" style={{ padding: '5px 15px', fontSize: '12px', marginTop: '10px' }} onClick={() => setUpdatingTaskId(task._id)}>Mark as Done</button>
                                    </div>
                                ))}
                                {pendingTasks.length === 0 && <p style={{ color: '#64748b' }}>No pending tasks.</p>}

                                <h5 style={{ fontWeight: '800', color: '#16a34a', marginTop: '30px', marginBottom: '15px' }}>Task History (Completed)</h5>
                                <div className="table-container" style={{ padding: '0', border: '1px solid #e2e8f0' }}>
                                    <table className="custom-zebra-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Task Name</th>
                                                <th>Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedDoneTasks.map(task => (
                                                <tr key={task._id}>
                                                    <td className="time-muted">{new Date(task.updatedAt || task.createdAt).toLocaleDateString()}</td>
                                                    <td className="date-bold" style={{ textDecoration: 'line-through' }}>{task.task_name}</td>
                                                    <td>
                                                        <div className="msg-body" style={{fontSize: '12px', marginBottom: '4px'}}><strong>Task:</strong> {task.task_note}</div>
                                                        <div className="msg-body" style={{fontSize: '12px', color: '#16a34a'}}><strong>Update:</strong> {task.update_note}</div>
                                                    </td>
                                                </tr>
                                            ))}
                                            {paginatedDoneTasks.length === 0 && <tr><td colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>No completed tasks.</td></tr>}
                                        </tbody>
                                    </table>
                                    <Pagination totalItems={doneTasks.length} currentPage={doneTaskPage} onPageChange={setDoneTaskPage} />
                                    <div style={{ height: '20px' }}></div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal for "Mark as Done" */}
            {updatingTaskId && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal">
                        <div className="modal-header-custom">
                            <h5>Complete Task</h5>
                            <button onClick={() => setUpdatingTaskId(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>&times;</button>
                        </div>
                        <div className="modal-body-custom">
                            <label className="field-label">Update Note (Required)</label>
                            <textarea className="modern-input" rows="3" placeholder="Describe how the task was completed..." value={updateNoteText} onChange={(e) => setUpdateNoteText(e.target.value)}></textarea>
                        </div>
                        <div className="modal-footer-custom">
                            <button className="modal-btn cancel" onClick={() => setUpdatingTaskId(null)}>Cancel</button>
                            <button className="modal-btn confirm" onClick={handleCompleteTask}>Submit Done</button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default AccountTaskManager;
